<template>
  <div class="d-flex color-picker">
    <b-form-group
      class="flex-grow-1 color-picker-group input-form-group"
    >
      <template #label>
        <span class="input-label">Color Family</span>
      </template>
      <CustomSelect
        id="color-family-dropdown"
        v-model="colorFamilyValue"
        show-arrow-icon
        placeholder="Select the color family"
        no-data-text="No color families found."
        theme="grey"
        size="small"
        :should-scroll-on-focus="true"
        :close-on-select="true"
        :option-groups="[{ header: '', options: colorFamilyOptions }]"
        :add-new-option="true"
        :clear-search-on-select="true"
        @custom-item="handleAddNewColorFamily"
        @input="(newColorFamily) => emitUpdate({ colorFamily: newColorFamily[0], from: 'input', all: newColorFamily })"
        @search="(search) => handleSearchColorFamily(search)"
        @select="(newColorFamily) => emitUpdate({ colorFamily: newColorFamily[0], from: 'select', all: newColorFamily })"
      />
    </b-form-group>

    <b-form-group
      class="flex-grow-1 color-picker-group input-form-group"
    >
      <template #label>
        <span class="input-label">Color *</span>
      </template>
      <ValidationProvider
        v-slot="{ errors, valid }"
        immediate
        name="Color"
        rules="required|min:1"
      >
        <b-form-input
          v-model="color"
          placeholder="Enter color name"
          :class="{ 'is-invalid': errors.length }"
          :state="!valid ? false : null"
          @input="handleChangeInput"
          @blur="syncColor"
        />
        <div class="error-section">
          <small class="text-danger">{{ errors[0] }}</small>
        </div>
      </ValidationProvider>
    </b-form-group>

    <b-form-group
      class="flex-grow-1 color-picker-group input-form-group"
    >
      <template #label>
        <span class="input-label">Hex Code</span>
      </template>
      <b-form-input
        v-model="hexCode"
        placeholder="e.g. #FF5733"
        @input="handleChangeInput"
        @blur="syncColor"
      />
    </b-form-group>
  </div>
</template>

<script>
import { BFormGroup, BFormInput } from 'bootstrap-vue'
import CustomSelect from '@core/components/custom-select/CustomSelect.vue'
import { ValidationProvider } from 'vee-validate'
import { productConstants } from '@kingpin-global/kingpin-utils-frontend'
import store from '@/store'
import { ITEMS_UPDATED, UPDATE_PRODUCT_INFO } from '@/store/modules/productEdit.module'

export default {
  name: 'ColorPicker',
  components: {
    CustomSelect,
    BFormGroup,
    BFormInput,
    ValidationProvider,
  },
  props: {
    value: {
      type: Object,
      required: true, // Must contain `colorFamily` and `color`
    },
    itemIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      colorFamilyOptions: [
        {
          title: '',
          options: productConstants.COLOR_FAMILY,
        },
      ],
      color: '',
      hexCode: '',
      colorFamilyValue: [],
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.color = newValue.color
        this.hexCode = newValue.hexCode || ''
        this.colorFamilyValue = newValue.colorFamily ? [newValue.colorFamily] : []
      },
      deep: true,
    },
  },
  methods: {
    handleChangeInput() {
      store.commit(ITEMS_UPDATED)
    },
    syncColor() {
      this.emitUpdate({
        color: this.color,
        colorFamily: this.colorFamilyValue[0] || '',
        hexCode: this.hexCode,
      })
    },

    emitUpdate(updatedFields = {}) {
      const { color = this.color, colorFamily = '', hexCode = this.hexCode } = updatedFields

      // Sync local state
      this.colorFamilyValue = colorFamily ? [colorFamily] : []

      this.updateColorState({ color, colorFamily, hexCode }, updatedFields)
    },

    updateColorState({ color, colorFamily, hexCode }, additionalFields = {}) {
      const items = this.$store.state.productEdit.newData.items
      if (items && items[this.itemIndex]) {
        const updatedItems = [...items]
        updatedItems[this.itemIndex] = {
          ...items[this.itemIndex],
          color,
          colorFamily,
          hexCode,
        }

        store.commit(UPDATE_PRODUCT_INFO, {
          fieldName: 'items',
          newValue: updatedItems,
        })
      }

      const emitData = {
        ...this.value,
        color,
        colorFamily,
        hexCode,
        ...additionalFields,
      }

      this.$emit('input', emitData)
    },
    handleSearchColorFamily(search) {
      const options = search.length === 0
        ? productConstants.COLOR_FAMILY
        : this.colorFamilyOptions[0].options.filter(option => option.toLowerCase().includes(search.toLowerCase()))

      this.colorFamilyOptions = [
        {
          title: '',
          options,
        },
      ]
    },
    handleAddNewColorFamily(newColorFamily) {
      this.emitUpdate({ colorFamily: newColorFamily })
      this.colorFamilyOptions = [
        {
          title: '',
          options: [...productConstants.COLOR_FAMILY, newColorFamily],
        },
      ]
    },
  },
}
</script>
<style>
.color-picker {
  gap: 16px;
  .color-picker-group {
    width: 100%;
  }
}
</style>
